import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useContent } from "@/lib/content";

export default function Signup() {
  const { t } = useContent();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const { data } = await api.post("/subscribe", { email, source: "landing", origin_url: window.location.origin });
      setDone(true);
      toast.success(data.message || "You're on the list.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="signup"
      data-testid="signup-section"
      className="bg-[#DFFF00] text-[#090909] scroll-mt-20 px-4 md:px-8 py-24 md:py-36 border-t-2 border-[#090909]"
    >
      <div className="max-w-5xl">
        <p className="overline mb-6">{t("signup_label")}</p>
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="font-display text-6xl md:text-8xl lg:text-9xl leading-[0.82] tracking-tight"
        >
          {t("signup_heading").split("\n").map((line, i) => (
            <span key={i} className="block">{line}</span>
          ))}
        </motion.h2>
        <p className="font-serif-ed italic text-xl md:text-2xl mt-6 max-w-xl">
          {t("signup_body")}
        </p>

        {done ? (
          <div
            data-testid="signup-success"
            className="mt-12 border-2 border-[#090909] bg-[#090909] text-[#DFFF00] p-6 font-mono-ui text-sm hard-shadow"
          >
            {t("signup_success")}
          </div>
        ) : (
          <form
            onSubmit={submit}
            data-testid="signup-form"
            className="mt-12 flex flex-col sm:flex-row items-stretch gap-4 max-w-2xl"
          >
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              data-testid="signup-email-input"
              className="flex-1 bg-transparent border-0 border-b-2 border-[#090909] px-1 py-3 h-14 font-mono-ui text-lg placeholder:text-[#090909]/50 focus:outline-none focus:border-[#FF2A00]"
            />
            <button
              type="submit"
              disabled={loading}
              data-testid="signup-submit-btn"
              className="h-14 px-8 font-mono-ui uppercase text-sm tracking-widest border-2 border-[#090909] bg-[#090909] text-[#F2EFEB] hover:bg-[#FF2A00] hover:border-[#FF2A00] transition-colors disabled:opacity-60"
            >
              {loading ? "Sending..." : t("signup_button")}
            </button>
          </form>
        )}
      </div>
    </section>
  );
}
